/**
 * As ações que uma unidade pode tomar, e a validação de cada uma.
 *
 * Uma ação cobre o turno inteiro da unidade — mover E o que ela faz ao chegar —
 * porque é assim que o jogador pensa e é assim que o servidor precisa validar:
 * um pacote só, aceito ou recusado inteiro, sem estado intermediário para
 * alguém explorar.
 */
import { resolveCombat, healAmount } from './combat.js';
import { combatantOf } from './combatant.js';
import { isChestKey, isConsumable, isUsableItem } from './content.js';
import { bodyFits, buildOccupancy } from './grid.js';
import { pathCost, reachableTiles } from './movement.js';
import { expForHeal, expForHit, expForKill, gainExp } from './progression.js';
import { applyCondition, curableBy, hasEffect, removeConditions, wakeOnDamage, } from './states.js';
import { chestAt, chestPrize, equippedWeapon, isWeaponSlot, rngOf, settleEquipped, slotDef, slotKey, unitById, weaponOf, withRng, withUnits, } from './state.js';
import { isHostile, positionKey, samePosition, spanDistance, } from './types.js';
/**
 * Quantos itens cabem numa bolsa.
 *
 * Cinco no Fire Emblem, quatro aqui — é o que o acervo distribui e o que a
 * ficha da unidade mostra. Vive no motor porque a troca precisa recusar o
 * quinto item, e recusar é decisão de regra, não de tela.
 */
export const MAX_INVENTORY = 4;
function fail(code, message) {
    return { ok: false, code, message };
}
/** Confere o deslocamento e devolve onde a unidade termina. */
function validateMove(state, content, unit, path) {
    if (path.length === 0)
        return { destination: unit.position };
    if (!samePosition(path[0], unit.position)) {
        return fail('caminho_invalido', 'O caminho não começa onde a unidade está.');
    }
    const occupancy = buildOccupancy(state.units);
    const cost = pathCost(state.map, content, occupancy, unit.team, path, unit.size);
    if (cost === null)
        return fail('caminho_invalido', 'O caminho passa por casa intransponível.');
    const movement = content.classes.get(unit.classId)?.move ?? 5;
    if (cost > movement) {
        return fail('movimento_excedido', `O caminho custa ${cost} e a unidade move ${movement}.`);
    }
    const destination = path[path.length - 1];
    // A casa do destino não basta para quem ocupa mais de uma: o corpo inteiro
    // tem de caber, e sem ninguém além dela mesma por baixo.
    if (!bodyFits(state.map, content, occupancy, destination, unit.id, unit.size)) {
        return fail('casa_ocupada', 'A casa de destino já está ocupada.');
    }
    // O alcance confirma o caminho: um caminho barato mas que sai da área
    // alcançável indicaria cliente adulterado, não erro de digitação.
    const range = reachableTiles(state.map, content, occupancy, unit.position, movement, unit.team, unit.size);
    if (!range.tiles.has(positionKey(destination))) {
        return fail('fora_de_alcance', 'O destino está fora do alcance de movimento.');
    }
    return { destination };
}
function checkActor(state, unitId) {
    const unit = unitById(state, unitId);
    if (!unit)
        return fail('unidade_desconhecida', 'Essa unidade não existe nesta partida.');
    if (!unit.alive)
        return fail('unidade_morta', 'A unidade está fora de combate.');
    if (unit.team !== state.phase)
        return fail('fora_da_fase', 'Não é a vez dessa equipe.');
    if (unit.hasActed)
        return fail('ja_agiu', 'A unidade já agiu neste turno.');
    if (hasEffect(unit.states, 'nao_age')) {
        return fail('impedida', 'A unidade está impedida de agir.');
    }
    return unit;
}
function spendUse(unit, count) {
    const item = equippedWeapon(unit);
    if (!item || count <= 0)
        return { unit, broke: null };
    const usesLeft = Math.max(0, item.usesLeft - count);
    const inventory = unit.inventory.map((it, i) => i === unit.equipped ? { ...it, usesLeft } : it);
    const cleaned = inventory.filter((it) => it.usesLeft > 0);
    const broke = usesLeft === 0 ? item.weaponId : null;
    return {
        unit: {
            ...unit,
            inventory: cleaned,
            // A arma que se partiu cede a vez à seguinte — que tem de ser ARMA: um
            // antídoto na mão não revida.
            equipped: settleEquipped(cleaned, Math.min(unit.equipped, Math.max(0, cleaned.length - 1))),
        },
        broke,
    };
}
/**
 * Gasta um uso do item nesse espaço da bolsa, sem mexer no que está equipado.
 *
 * `spendUse` só sabe cobrar da arma em uso; a poção é usada de dentro da
 * bolsa, e o buraco que ela deixa ao acabar desloca os índices seguintes. Por
 * isso a arma equipada é reencontrada pela posição antiga, e não copiada: sem
 * isso, beber a última poção poderia trocar a espada por um cajado.
 */
function consumeAt(unit, index) {
    const item = unit.inventory[index];
    if (!item)
        return unit;
    const kept = [];
    unit.inventory.forEach((it, i) => {
        const usesLeft = i === index ? it.usesLeft - 1 : it.usesLeft;
        if (usesLeft > 0)
            kept.push({ item: { ...it, usesLeft }, from: i });
    });
    const bag = kept.map((entry) => entry.item);
    const equipped = kept.findIndex((entry) => entry.from === unit.equipped);
    return {
        ...unit,
        inventory: bag,
        equipped: settleEquipped(bag, equipped >= 0 ? equipped : 0),
    };
}
/**
 * Esta unidade aprende alguma coisa nesta partida?
 *
 * Três respostas, e cada modalidade escolhe a sua (ver `MatchState.progression`):
 *
 *   ausente     as duas equipes aprendem. É a partida entre pessoas, onde os
 *               dois lados são jogadores de verdade e premiar só um deles daria
 *               vantagem a quem calhasse de ser a equipe "jogador".
 *   `false`     ninguém aprende. Escaramuça (teste) e desafio online (disputa).
 *   `'jogador'` só o seu lado. É a campanha: do outro lado está a máquina, e
 *               um exército que sobe de nível enquanto o jogador pensa
 *               transforma demora em punição.
 */
function learnsHere(state, unit) {
    if (state.progression === false)
        return false;
    if (state.progression === 'jogador')
        return unit.team === 'jogador';
    return true;
}
function awardExp(content, unit, amount, rngState, progression = true) {
    // Uma partida sem progressão não distribui experiência nenhuma — nem o
    // evento. Ver `MatchState.progression`.
    if (!progression)
        return { unit, events: [] };
    const klass = content.classes.get(unit.classId);
    if (!klass || unit.team === 'neutro' || amount <= 0)
        return { unit, events: [] };
    const result = gainExp(klass, unit.level, unit.exp, unit.stats, amount, rngState.rng);
    const next = {
        ...unit,
        level: result.level,
        exp: result.exp,
        stats: result.stats,
        // Subir de nível cura o que o atributo de PV ganhou: perder um nível de PV
        // por estar ferido seria punir quem lutou.
        currentHp: unit.currentHp + (result.stats.hp - unit.stats.hp),
    };
    const events = [
        {
            type: 'ganhou_exp',
            unitId: unit.id,
            amount,
            exp: result.exp,
            level: result.level,
        },
    ];
    if (result.leveled) {
        events.push({
            type: 'subiu_nivel',
            unitId: unit.id,
            level: result.level,
            gains: result.gains,
        });
    }
    return { unit: next, events };
}
export function applyAction(state, content, action) {
    if (state.status !== 'em_andamento' && action.type !== 'encerrar_fase') {
        return fail('partida_encerrada', 'A partida já acabou.');
    }
    switch (action.type) {
        case 'trocar_arma': {
            const actor = checkActor(state, action.unitId);
            if ('ok' in actor)
                return actor;
            if (action.index < 0 || action.index >= actor.inventory.length) {
                return fail('item_invalido', 'Não há arma nesse espaço do inventário.');
            }
            if (!isWeaponSlot(actor.inventory[action.index])) {
                return fail('item_nao_equipavel', 'Esse item não se empunha: ele se usa.');
            }
            const unit = { ...actor, equipped: action.index };
            return {
                ok: true,
                state: withUnits(state, [unit]),
                events: [{ type: 'trocou_arma', unitId: unit.id, index: action.index }],
            };
        }
        case 'mover': {
            const actor = checkActor(state, action.unitId);
            if ('ok' in actor)
                return actor;
            const move = validateMove(state, content, actor, action.path);
            if ('ok' in move)
                return move;
            const unit = { ...actor, position: move.destination, hasActed: true };
            // Ficar parado não é mover. O cliente anima as duas coisas de formas
            // diferentes, e um "moveu" com caminho vazio obrigaria cada consumidor a
            // adivinhar o que fazer com ele.
            const moved = action.path.length > 1;
            return {
                ok: true,
                state: withUnits(state, [unit]),
                events: [
                    moved
                        ? { type: 'moveu', unitId: unit.id, path: action.path }
                        : { type: 'esperou', unitId: unit.id },
                ],
            };
        }
        case 'atacar':
            return applyAttack(state, content, action);
        case 'curar':
            return applyHeal(state, content, action);
        case 'usar_item':
            return applyUseItem(state, content, action);
        case 'abrir_bau':
            return applyOpenChest(state, content, action);
        case 'trocar_itens':
            return applyTrade(state, content, action);
        case 'conversar':
            return applyTalk(state, content, action);
        case 'encerrar_fase':
            return { ok: true, state, events: [{ type: 'fase_encerrada', team: state.phase }] };
    }
}
/**
 * Quem desta equipe consegue convencer esta unidade a mudar de lado?
 *
 * A resposta é do motor porque a tela precisa da MESMA resposta para decidir
 * se oferece a ordem "Conversar": um menu que oferece o que o motor recusa é
 * pior do que um menu curto.
 */
export function canTalkTo(actor, target) {
    if (!target.recruitable || !target.alive || !actor.alive)
        return false;
    if (!isHostile(actor.team, target.team))
        return false;
    const by = target.recruitable.by;
    // Sem lista, qualquer um do lado de cá serve — é o capítulo que decide se
    // quer a conversa restrita a um personagem.
    return by === undefined || by.length === 0 || by.includes(actor.id);
}
/**
 * A conversa que muda alguém de lado.
 *
 * Gasta o turno de quem falou e NÃO gasta o de quem foi convencido: ele entra
 * com a ação livre e pode jogar ainda nesta fase, que é como a série trata o
 * recruta. A ficha atravessa inteira — nível, atributos, bolsa e condições —
 * porque quem chega é a mesma pessoa que estava do outro lado um instante
 * antes; só a bandeira muda.
 */
function applyTalk(state, content, action) {
    const actor = checkActor(state, action.unitId);
    if ('ok' in actor)
        return actor;
    const target = unitById(state, action.targetId);
    if (!target || !target.alive)
        return fail('alvo_invalido', 'O alvo não está em combate.');
    if (!target.recruitable) {
        return fail('alvo_nao_recrutavel', `Não há o que conversar com ${target.name}.`);
    }
    if (!isHostile(actor.team, target.team)) {
        return fail('alvo_aliado', 'Essa unidade já está do seu lado.');
    }
    if (!canTalkTo(actor, target)) {
        return fail('conversa_recusada', `${target.name} não ouve ${actor.name}.`);
    }
    const move = validateMove(state, content, actor, action.path);
    if ('ok' in move)
        return move;
    // Encostado, como a troca: convencer alguém é coisa que se faz de perto.
    const reach = spanDistance(move.destination, actor.size, target.position, target.size);
    if (reach !== 1) {
        return fail('fora_de_alcance', `Chegue ao lado de ${target.name} para conversar.`);
    }
    const joins = target.recruitable.joins ?? 'jogador';
    const talker = { ...actor, position: move.destination, hasActed: true };
    const recruited = {
        ...target,
        team: joins,
        hasActed: false,
        // O convite não se repete: a unidade já está do lado de cá.
        recruitable: undefined,
        // E ela deixa de lutar como a máquina do capítulo mandava — quem comanda
        // agora é quem a convenceu.
        aiProfile: 'defensivo',
    };
    const events = [];
    if (action.path.length > 1)
        events.push({ type: 'moveu', unitId: actor.id, path: action.path });
    events.push({ type: 'recrutou', unitId: actor.id, targetId: target.id, team: joins });
    return { ok: true, state: withUnits(state, [talker, recruited]), events };
}
function applyAttack(state, content, action) {
    const actor = checkActor(state, action.unitId);
    if ('ok' in actor)
        return actor;
    const target = unitById(state, action.targetId);
    if (!target || !target.alive)
        return fail('alvo_invalido', 'O alvo não está em combate.');
    if (!isHostile(actor.team, target.team))
        return fail('alvo_aliado', 'Essa unidade não é inimiga.');
    const move = validateMove(state, content, actor, action.path);
    if ('ok' in move)
        return move;
    const weapon = weaponOf(content, actor);
    if (!weapon)
        return fail('sem_arma', 'A unidade não tem arma utilizável.');
    if (weapon.heals)
        return fail('arma_de_cura', 'Um cajado de cura não ataca.');
    if (weapon.magic && hasEffect(actor.states, 'sem_magia')) {
        return fail('silenciado', `${actor.name} está silenciado: a arma mágica não responde.`);
    }
    const reach = spanDistance(move.destination, actor.size, target.position, target.size);
    if (reach < weapon.range[0] || reach > weapon.range[1]) {
        return fail('fora_de_alcance', `O alvo está a ${reach} e a arma alcança ${weapon.range.join('–')}.`);
    }
    const moved = { ...actor, position: move.destination };
    const rng = rngOf(state);
    const result = resolveCombat(content, combatantOf(state, content, moved), combatantOf(state, content, target), reach, rng);
    const events = [];
    if (action.path.length > 1)
        events.push({ type: 'moveu', unitId: actor.id, path: action.path });
    events.push({
        type: 'combate',
        attackerId: actor.id,
        defenderId: target.id,
        blows: result.blows,
        forecast: result.forecast,
    });
    /*
     * O que o confronto fez às condições de cada lado, nesta ordem: primeiro o
     * dano acorda quem dormia, depois entra o que os golpes causaram. Na ordem
     * inversa, o golpe que faz dormir e fere ao mesmo tempo acordaria o alvo
     * no mesmo instante.
     */
    const settleConditions = (unit, hpBefore, side) => {
        if (!unit.alive)
            return unit;
        let states = unit.states;
        if (unit.currentHp < hpBefore) {
            const woken = wakeOnDamage(states);
            states = woken.kept;
            for (const condition of woken.woke) {
                events.push({ type: 'condicao_removida', unitId: unit.id, condition, reason: 'acordou' });
            }
        }
        for (const hit of result.afflicted) {
            if (hit.side !== side)
                continue;
            states = applyCondition(states, hit.condition, hit.turns);
            events.push({
                type: 'condicao_aplicada',
                unitId: unit.id,
                condition: hit.condition,
                turns: hit.turns,
                byId: side === 'atacante' ? target.id : actor.id,
            });
        }
        return states === unit.states ? unit : { ...unit, states };
    };
    let attacker = settleConditions({
        ...moved,
        currentHp: result.attackerHpAfter,
        alive: result.attackerHpAfter > 0,
        hasActed: true,
    }, moved.currentHp, 'atacante');
    let defender = settleConditions({
        ...target,
        currentHp: result.defenderHpAfter,
        alive: result.defenderHpAfter > 0,
    }, target.currentHp, 'defensor');
    const attackerSpend = spendUse(attacker, result.attackerUses);
    attacker = attackerSpend.unit;
    if (attackerSpend.broke !== null) {
        events.push({ type: 'arma_quebrou', unitId: attacker.id, weaponId: attackerSpend.broke });
    }
    const defenderSpend = spendUse(defender, result.defenderUses);
    defender = defenderSpend.unit;
    if (defenderSpend.broke !== null) {
        events.push({ type: 'arma_quebrou', unitId: defender.id, weaponId: defenderSpend.broke });
    }
    if (!defender.alive)
        events.push({ type: 'morreu', unitId: defender.id });
    if (!attacker.alive)
        events.push({ type: 'morreu', unitId: attacker.id });
    // A experiência sai do dano causado; matar rende bem mais. Quem morreu no
    // contra-ataque não aprende nada — a lição chegou tarde.
    const carrier = { rng };
    const dealtDamage = result.blows.some((b) => b.side === 'atacante' && b.damage > 0);
    if (attacker.alive && dealtDamage) {
        const amount = defender.alive
            ? expForHit(attacker.level, defender.level)
            : expForKill(attacker.level, defender.level, defender.isCommander);
        const awarded = awardExp(content, attacker, amount, carrier, learnsHere(state, attacker));
        attacker = awarded.unit;
        if (awarded.events.length)
            events.push(...awarded.events);
    }
    if (defender.alive && result.blows.some((b) => b.side === 'defensor' && b.damage > 0)) {
        const amount = attacker.alive
            ? expForHit(defender.level, attacker.level)
            : expForKill(defender.level, attacker.level, attacker.isCommander);
        const awarded = awardExp(content, defender, amount, carrier, learnsHere(state, defender));
        defender = awarded.unit;
        if (awarded.events.length)
            events.push(...awarded.events);
    }
    return { ok: true, state: withRng(withUnits(state, [attacker, defender]), rng), events };
}
function applyHeal(state, content, action) {
    const actor = checkActor(state, action.unitId);
    if ('ok' in actor)
        return actor;
    const target = unitById(state, action.targetId);
    if (!target || !target.alive)
        return fail('alvo_invalido', 'O alvo não está em combate.');
    if (isHostile(actor.team, target.team))
        return fail('alvo_inimigo', 'Não dá para curar um inimigo.');
    const weapon = weaponOf(content, actor);
    if (!weapon?.heals)
        return fail('sem_cajado', 'A unidade não tem cajado de cura equipado.');
    if (hasEffect(actor.states, 'sem_magia')) {
        return fail('silenciado', `${actor.name} está silenciado: o cajado não responde.`);
    }
    // O cajado que remove condições serve também a quem está de PV cheio: o
    // aliado paralisado não precisa de vida, precisa voltar a se mexer.
    const cured = curableBy(target.states, weapon.cures);
    if (target.currentHp >= target.stats.hp && cured.length === 0) {
        return fail('alvo_ileso', 'O alvo já está com os PV cheios.');
    }
    const move = validateMove(state, content, actor, action.path);
    if ('ok' in move)
        return move;
    const targetPos = target.id === actor.id ? move.destination : target.position;
    const reach = spanDistance(move.destination, actor.size, targetPos, target.size);
    if (reach < weapon.range[0] || reach > weapon.range[1]) {
        return fail('fora_de_alcance', `O alvo está a ${reach} e o cajado alcança ${weapon.range.join('–')}.`);
    }
    const moved = { ...actor, position: move.destination };
    const amount = Math.min(healAmount(combatantOf(state, content, moved)), target.stats.hp - target.currentHp);
    const rng = rngOf(state);
    const events = [];
    if (action.path.length > 1)
        events.push({ type: 'moveu', unitId: actor.id, path: action.path });
    events.push({
        type: 'curou', healerId: actor.id, targetId: target.id, amount,
        ...(cured.length > 0 ? { cured } : {}),
    });
    for (const condition of cured) {
        events.push({ type: 'condicao_removida', unitId: target.id, condition, reason: 'curada' });
    }
    let healer = { ...moved, hasActed: true };
    const spend = spendUse(healer, 1);
    healer = spend.unit;
    if (spend.broke !== null) {
        events.push({ type: 'arma_quebrou', unitId: healer.id, weaponId: spend.broke });
    }
    const awarded = awardExp(content, healer, expForHeal(amount), { rng }, learnsHere(state, healer));
    healer = awarded.unit;
    if (awarded.events.length)
        events.push(...awarded.events);
    // Quem cura a si mesmo é uma unidade só: o alvo parte do curandeiro já
    // gasto, e não da ficha de antes, senão a gravação de um apagaria a do outro.
    const base = target.id === healer.id ? healer : target;
    const healed = {
        ...base,
        currentHp: base.currentHp + amount,
        ...(cured.length > 0 ? { states: removeConditions(base.states, cured) } : {}),
    };
    return {
        ok: true,
        state: withRng(withUnits(state, target.id === healer.id ? [healed] : [healer, healed]), rng),
        events,
    };
}
export function itemUseOf(content, slot) {
    const found = slotDef(content, slot);
    if (!found)
        return null;
    if (found.kind === 'arma') {
        if (!isConsumable(found.def))
            return null;
        return {
            heal: found.def.might,
            healRate: 0,
            cures: found.def.cures ?? [],
            range: found.def.range,
            weaponId: found.def.id,
        };
    }
    if (!isUsableItem(found.def))
        return null;
    return {
        heal: found.def.heal ?? 0,
        healRate: found.def.healRate ?? 0,
        cures: found.def.cures ?? [],
        // O RPG Maker diz "um aliado"; num tabuleiro, é quem está encostado.
        range: [0, 1],
        itemId: found.def.id,
    };
}
/** Quantos PV este uso devolve a quem tem estes PV, sem passar do máximo. */
export function itemHealFor(use, target) {
    const missing = Math.max(0, target.stats.hp - target.currentHp);
    const raw = use.heal + Math.floor(target.stats.hp * use.healRate);
    return Math.min(missing, raw);
}
/** O uso faz alguma diferença para este alvo? Cura PV que faltam, ou condição que ele tem. */
export function itemHelps(use, target) {
    return itemHealFor(use, target) > 0 || curableBy(target.states, use.cures).length > 0;
}
/**
 * Usar um item: cura onde a unidade parou — ela mesma ou o aliado ao lado — e
 * a jogada acaba aí.
 *
 * O item nunca vai para a mão. Antes, a única forma de beber a poção era
 * equipá-la — e a unidade terminava o turno segurando um frasco vazio no lugar
 * da espada, indefesa no contra-ataque.
 */
function applyUseItem(state, content, action) {
    const actor = checkActor(state, action.unitId);
    if ('ok' in actor)
        return actor;
    const item = actor.inventory[action.index];
    if (!item || item.usesLeft <= 0) {
        return fail('item_invalido', 'Não há item nesse espaço da bolsa.');
    }
    const use = itemUseOf(content, item);
    if (!use) {
        return fail('item_nao_consumivel', isWeaponSlot(item) ? 'Esse item não se usa: ele se equipa.' : 'Esse item não tem efeito em campo.');
    }
    const targetId = action.targetId ?? actor.id;
    const self = targetId === actor.id;
    const target = self ? actor : unitById(state, targetId);
    if (!target || !target.alive)
        return fail('alvo_invalido', 'O alvo não está em combate.');
    if (!self && isHostile(actor.team, target.team)) {
        return fail('alvo_inimigo', 'Item de cura não se usa num inimigo.');
    }
    if (!itemHelps(use, target)) {
        return use.cures.length > 0
            ? fail('sem_efeito', `Nada para curar em ${target.name}.`)
            : fail('ileso', `${target.name} já está com os PV cheios.`);
    }
    const move = validateMove(state, content, actor, action.path);
    if ('ok' in move)
        return move;
    if (!self) {
        const reach = spanDistance(move.destination, actor.size, target.position, target.size);
        if (reach < Math.max(1, use.range[0]) || reach > use.range[1]) {
            return fail('fora_de_alcance', 'O item só alcança quem está encostado.');
        }
    }
    const amount = itemHealFor(use, target);
    const cured = curableBy(target.states, use.cures);
    const events = [];
    if (action.path.length > 1)
        events.push({ type: 'moveu', unitId: actor.id, path: action.path });
    events.push({
        type: 'usou_item',
        unitId: actor.id,
        targetId: target.id,
        ...(use.weaponId !== undefined ? { weaponId: use.weaponId } : {}),
        ...(use.itemId !== undefined ? { itemId: use.itemId } : {}),
        amount,
        cured,
    });
    for (const condition of cured) {
        events.push({ type: 'condicao_removida', unitId: target.id, condition, reason: 'curada' });
    }
    const treat = (unit) => ({
        ...unit,
        currentHp: unit.currentHp + amount,
        ...(cured.length > 0 ? { states: removeConditions(unit.states, cured) } : {}),
    });
    const rng = rngOf(state);
    let user = consumeAt({ ...actor, position: move.destination, hasActed: true }, action.index);
    if (self)
        user = treat(user);
    const awarded = awardExp(content, user, expForHeal(amount), { rng }, learnsHere(state, user));
    user = awarded.unit;
    if (awarded.events.length)
        events.push(...awarded.events);
    const changed = self ? [user] : [user, treat(target)];
    return { ok: true, state: withRng(withUnits(state, changed), rng), events };
}
/** Quem pode abrir um baú trancado, e com o quê. */
export function chestOpener(content, unit) {
    // O ladrão abre com as mãos: é a razão de a classe existir num Fire Emblem,
    // e o que faz valer a pena levá-lo num esquadrão de quatro.
    const archetype = content.classes.get(unit.classId)?.archetype;
    if (archetype === 'ladrao')
        return { can: true, keyIndex: null };
    // A chave pode ser a arma tática (53) ou o item do RPG — os dois abrem.
    const keyIndex = unit.inventory.findIndex((item) => item.usesLeft > 0 && isChestKey(slotDef(content, item)?.def));
    return keyIndex >= 0 ? { can: true, keyIndex } : { can: false, keyIndex: null };
}
/**
 * Abrir o baú da casa onde a unidade parou.
 *
 * O item vai para a bolsa de quem abriu — e é por isso que a bolsa cheia
 * RECUSA a abertura em vez de abrir e perder o conteúdo: um baú aberto não
 * volta a fechar, e deixar o prêmio cair no chão seria puni-lo por ter
 * inventário.
 */
function applyOpenChest(state, content, action) {
    const actor = checkActor(state, action.unitId);
    if ('ok' in actor)
        return actor;
    const move = validateMove(state, content, actor, action.path);
    if ('ok' in move)
        return move;
    const chest = chestAt(state, move.destination);
    if (!chest)
        return fail('sem_bau', 'Não há baú fechado nessa casa.');
    const opener = chestOpener(content, actor);
    const locked = chest.locked !== false;
    if (locked && !opener.can) {
        return fail('bau_trancado', `${actor.name} não tem chave, e não é ladrão para abrir sem uma.`);
    }
    const prize = chestPrize(content, chest);
    if (!prize)
        return fail('bau_vazio', 'O item deste baú não existe no acervo.');
    if (actor.inventory.length >= MAX_INVENTORY) {
        return fail('bolsa_cheia', `A bolsa de ${actor.name} está cheia — largue algo antes de abrir.`);
    }
    const usedKey = locked && opener.keyIndex !== null;
    let unit = {
        ...actor,
        position: move.destination,
        hasActed: true,
        inventory: [...actor.inventory, prize.slot],
    };
    if (usedKey && opener.keyIndex !== null)
        unit = consumeAt(unit, opener.keyIndex);
    const events = [];
    if (action.path.length > 1)
        events.push({ type: 'moveu', unitId: actor.id, path: action.path });
    events.push({
        type: 'abriu_bau',
        unitId: actor.id,
        chestId: chest.id,
        ...(prize.slot.weaponId !== undefined
            ? { weaponId: prize.slot.weaponId }
            : { itemId: prize.slot.itemId }),
        usedKey,
    });
    const chests = (state.chests ?? []).map((c) => c.id === chest.id ? { ...c, opened: true, openedBy: actor.id } : c);
    return { ok: true, state: { ...withUnits(state, [unit]), chests }, events };
}
/**
 * A troca de itens entre duas unidades da mesma equipe.
 *
 * O motor não acompanha a arrumação: recebe as duas bolsas prontas e confere
 * três coisas — que os dois estão encostados, que nenhuma bolsa estourou, e que
 * o conjunto dos itens é exatamente o que era. Essa última é a que impede o
 * cliente adulterado de multiplicar poções: nada entra e nada some numa troca,
 * só muda de dono.
 */
function bagKey(item) {
    // Pelo que o espaço É, arma ou item: a arma 7 e o item 7 não são a mesma
    // coisa, e a troca que transformasse um no outro inventaria item.
    return `${slotKey(item)}:${item.usesLeft}`;
}
function sameContents(a, b) {
    if (a.length !== b.length)
        return false;
    const tally = new Map();
    for (const item of a) {
        const key = bagKey(item);
        tally.set(key, (tally.get(key) ?? 0) + 1);
    }
    for (const item of b) {
        const key = bagKey(item);
        const left = tally.get(key);
        if (!left)
            return false;
        if (left === 1)
            tally.delete(key);
        else
            tally.set(key, left - 1);
    }
    return tally.size === 0;
}
/**
 * Onde a arma equipada foi parar depois da troca.
 *
 * O índice antigo não serve: passar o primeiro item para o vizinho desloca
 * todos os seguintes, e a unidade terminaria a troca empunhando outra coisa —
 * ou um espaço vazio. Ela é reencontrada pelo que é. Se saiu da bolsa, a
 * unidade equipa o que sobrou na frente, como faz quando uma arma se parte.
 */
function reequip(previous, bag) {
    if (bag.length === 0)
        return 0;
    if (!previous)
        return settleEquipped(bag, 0);
    const found = bag.findIndex((item) => bagKey(item) === bagKey(previous));
    return settleEquipped(bag, found >= 0 ? found : 0);
}
function applyTrade(state, content, action) {
    const actor = checkActor(state, action.unitId);
    if ('ok' in actor)
        return actor;
    if (action.targetId === actor.id) {
        return fail('alvo_invalido', 'Uma unidade não troca itens consigo mesma.');
    }
    const target = unitById(state, action.targetId);
    if (!target || !target.alive)
        return fail('alvo_invalido', 'O alvo não está em combate.');
    // Só entre unidades da mesma equipe: o aliado da IA luta do seu lado, mas a
    // bolsa dele não é sua, e o inimigo muito menos.
    if (target.team !== actor.team) {
        return fail('alvo_de_outra_equipe', 'Só dá para trocar itens com uma unidade da sua equipe.');
    }
    const move = validateMove(state, content, actor, action.path);
    if ('ok' in move)
        return move;
    const reach = spanDistance(move.destination, actor.size, target.position, target.size);
    if (reach !== 1) {
        return fail('fora_de_alcance', 'A troca é de mão em mão: é preciso parar ao lado da unidade.');
    }
    if (action.actorInventory.length > MAX_INVENTORY ||
        action.targetInventory.length > MAX_INVENTORY) {
        return fail('bolsa_cheia', `Uma bolsa leva no máximo ${MAX_INVENTORY} itens.`);
    }
    if (action.actorInventory.some((it) => it.usesLeft <= 0) ||
        action.targetInventory.some((it) => it.usesLeft <= 0)) {
        return fail('item_invalido', 'Item gasto não vai para bolsa nenhuma.');
    }
    if (!sameContents([...actor.inventory, ...target.inventory], [...action.actorInventory, ...action.targetInventory])) {
        return fail('troca_invalida', 'A troca inventa ou perde item: só é permitido mudar de dono.');
    }
    const actorHeld = equippedWeapon(actor);
    const targetHeld = equippedWeapon(target);
    const moved = {
        ...actor,
        position: move.destination,
        inventory: action.actorInventory,
        equipped: reequip(actorHeld, action.actorInventory),
        hasActed: true,
    };
    // Quem recebe não gasta o turno: a troca é a jogada de quem a propôs.
    const partner = {
        ...target,
        inventory: action.targetInventory,
        equipped: reequip(targetHeld, action.targetInventory),
    };
    const events = [];
    if (action.path.length > 1)
        events.push({ type: 'moveu', unitId: actor.id, path: action.path });
    const stayed = (bag, before) => {
        const kept = [...before];
        let matched = 0;
        for (const item of bag) {
            const at = kept.findIndex((it) => bagKey(it) === bagKey(item));
            if (at >= 0) {
                kept.splice(at, 1);
                matched++;
            }
        }
        return matched;
    };
    events.push({
        type: 'trocou_itens',
        unitId: actor.id,
        targetId: target.id,
        given: actor.inventory.length - stayed(action.actorInventory, actor.inventory),
        taken: target.inventory.length - stayed(action.targetInventory, target.inventory),
    });
    return { ok: true, state: withUnits(state, [moved, partner]), events };
}
//# sourceMappingURL=actions.js.map